import { InlineIcon } from "@iconify/react";
import { Form, Formik } from "formik";
import _ from "lodash";
import { DateTime } from "luxon";
import React, { useContext, useState } from "react";
import { createUseStyles } from "react-jss";
import { useSelector } from "react-redux";
import { GlobalModalsContext, NoScrollContext } from "../../context";
import jssVariables from "../../jss_setup";
import { modalGenericStyles } from "../../jss_setup/modals";
import {
  StyleTypeContext,
  TranslationContext,
  countryCodes,
} from "../../umbilical_tools";
import { SearchButton } from "../common/Button";
import {
  AirportAutoCompleteField,
  DatepickerField,
  POIAutoCompleteInputField,
  ReactSelectField,
} from "../forms/inputGroup/helpers";
import { PaxPicker } from "../MainSearchForm/widgets";
import { tripModeOptions } from "../MainSearchForm/transfer";
import { createInitialValues } from "../MainSearchForm/transfer/helpers";
import PaxPickerModal from "../MainSearchForm/modals/PaxPickerModal";

const styles = createUseStyles({
  ...modalGenericStyles,
  DeepTransferSearchForm: (props) => ({
    ...modalGenericStyles.modal,
    position: "fixed",
    height: "100vh",
    width: "100vw",
    zIndex: 9999,
    ..._.get(props, "customStyles.DeepTransferSearchForm", {}),
  }),
  searchModal: (props) => ({
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90vw",
    background: jssVariables.colors.background.light,
    padding: jssVariables.normal_gap,
    borderRadius: jssVariables.border_radius,
    ..._.get(props, "customStyles.searchModal", {}),
  }),
  header: (props) => ({
    display: "grid",
    gridTemplateColumns: "repeat(2, max-content)",
    justifyContent: "space-between",
    fontSize: "large",
    fontWeight: "bold",
    padding: [0, jssVariables.normal_gap],
    color: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.header", {}),
  }),
  transferSearchForm: (props) => ({
    display: "grid",
    gridTemplateRows: "repeat(4, max-content)",
    gridGap: jssVariables.normal_gap,
    padding: jssVariables.normal_gap,
    alignItems: "center",
    ..._.get(props, "customStyles.transferSearchForm", {}),
  }),
  inputFields: (props) => ({
    ..._.get(props, "customStyles.inputFields", {}),
  }),
  closeIcon: (props) => ({
    fontSize: "xx-large",
    cursor: "pointer",
    ..._.get(props, "customStyles.closeIcon", {}),
  }),
  searchBtnContr: (props) => ({
    width: "max-content",
    margin: "auto",
    ..._.get(props, "customStyles.searchBtnContr", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    searchModal: (props) => ({
      width: "70rem",
      ..._.get(props, "customStyles.searchModal.smallscreen", {}),
    }),
    transferSearchForm: (props) => ({
      gridTemplateColumns: "1fr max-content",
      alignItems: "end",
      gridGap: jssVariables.half_gap,
      ..._.get(
        props,
        "customStyles.transferSearchForm.smallscreen.smallscreen",
        {}
      ),
    }),
    inputFields: (props) => ({
      display: "grid",
      gridTemplateRows: "unset",
      gridTemplateColumns: "max-content repeat(2, 1fr)",
      alignItems: "end",
      gridGap: jssVariables.half_gap,
      ..._.get(props, "customStyles.inputFields.smallscreen", {}),
    }),
    searchBtnContr: (props) => ({
      margin: "unset",
      paddingBottom: "1px",
      ..._.get(props, "customStyles.searchBtnContr.smallscreen", {}),
    }),
  },
});
const DeepTransferSearchForm = () => {
  const [showPaxPickerModal, setShowPaxPickerModal] = useState(false);

  const { t } = useContext(TranslationContext);
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const { setShowTrfFormModal } = useContext(GlobalModalsContext);

  const customStyles = cStyler({
    type,
    brand,
    objName: "DeepTransferSearchForm",
  });

  const classes = styles({
    customStyles,
  });

  const search_form = useSelector((state) => state.TransfersSearchForm);
  const [tripMode, setTripMode] = useState(search_form.tripMode);

  const [pax, setPax] = useState({
    adults: search_form.adults,
    children: search_form.children,
    children_ages: search_form.children_ages,
  });

  function createPayload(form) {
    var payload = {
      inb: form.inboundPickupDate,
      adt: form.pax.adults,
      chd: form.pax.children,
      ages: form.pax.children_ages,
      mode: form.tripMode,
    };

    if (form.tripMode === "air_to_poi") {
      payload = {
        ...payload,
        orgn_lat: form.origin.latitude,
        orgn_lng: form.origin.longitude,
        orgn_nm: form.origin.label,
        orgn_cd: form.origin.country_code,
        orgn_iata: form.origin.iata,
        orgn_loc_type: form.origin.location_type,

        dest_lat: form.destination.computed_data.geodata.lat,
        dest_lng: form.destination.computed_data.geodata.lng,
        dest_nm: form.destination.label,
        dest_cd: countryCodes[form.destination.country],
        dest_loc_type: form.origin.location_type,
      };
    } else {
      payload = {
        ...payload,
        orgn_code: form.origin.origin_code,
        orgn_lat: form.origin.computed_data.geodata.lat,
        orgn_lng: form.origin.computed_data.geodata.lng,
        orgn_nm: form.origin.label,
        orgn_cd: countryCodes[form.origin.country],
        orgn_loc_type: form.origin.location_type,

        dest_lat: form.destination.latitude,
        dest_lng: form.destination.longitude,
        dest_nm: form.destination.label,
        dest_cd: form.destination.country_code,
        dest_iata: form.destination.iata,
        dest_loc_type: form.origin.location_type,
      };
    }
    return payload;
  }

  async function onNewSearch(form) {
    const encoded_data = window.btoa(JSON.stringify(createPayload(form)));
    window.open(`/deep-transfer-search/${encoded_data}`, "_blank");
    setShowTrfFormModal(false);
  }

  function onClickPax() {
    setShowPaxPickerModal(true);
  }

  return (
    <div className={classes.DeepTransferSearchForm}>
      <div className={classes.searchModal}>
        <div className={classes.header}>
          <span>{t("performNewSearch")}</span>
          <InlineIcon
            icon="ei:close"
            className={classes.closeIcon}
            onClick={() => setShowTrfFormModal(false)}
          />
        </div>
        <Formik
          initialValues={createInitialValues({ search_form, tripMode, t })}
          validate={(values) => {
            if (values.tripMode !== tripMode) {
              setTripMode(values.tripMode.value);
            }
          }}
          onSubmit={async (values) => {
            var form = {
              ...values,
              origin:
                tripMode === "air_to_poi"
                  ? values.origin.origin_meta[0]
                  : {
                      ...values.origin.origin_meta,
                      country_code:
                        countryCodes[values.origin.origin_meta.country],
                    },

              destination:
                tripMode === "air_to_poi"
                  ? {
                      ...values.destination.destination_meta,
                      country_code:
                        countryCodes[
                          values.destination.destination_meta.country
                        ],
                    }
                  : values.destination.destination_meta[0],
              inboundPickupDate: values.inboundPickupDate[0],
              tripMode: values.tripMode.value,
              init_search: true,
              pax,
            };

            onNewSearch(form);
          }}>
          <Form className={classes.transferSearchForm}>
            <div className={classes.inputFields}>
              <ReactSelectField
                label={t("tripMode")}
                name="tripMode"
                options={tripModeOptions(t)}
                customStyles={_.get(customStyles, "inputGroupField", {})}
              />
              {tripMode === "air_to_poi" || tripMode === "round_trip" ? (
                <React.Fragment>
                  <AirportAutoCompleteField
                    label={t("airport")}
                    name="origin"
                    placeholder={t("airport")}
                    withErrors={false}
                    customStyles={_.get(customStyles, "inputGroupField", {})}
                  />
                  <POIAutoCompleteInputField
                    label={t("destinationPoint")}
                    name={"destination"}
                    types={["DEST"]}
                    placeholder={"----"}
                    customStyles={_.get(customStyles, "inputGroupField", {})}
                  />
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <POIAutoCompleteInputField
                    label={t("originPoint")}
                    name="origin"
                    types={["DEST"]}
                    placeholder={"----"}
                    isInFilters={true}
                    customStyles={_.get(customStyles, "inputGroupField", {})}
                  />
                  <AirportAutoCompleteField
                    label={t("airport")}
                    name="destination"
                    placeholder={t("airport")}
                    withErrors={false}
                    customStyles={_.get(customStyles, "inputGroupField", {})}
                  />
                </React.Fragment>
              )}
              <div />
              <DatepickerField
                label={t("inboundPickupDate")}
                name="inboundPickupDate"
                selectsRange={false}
                dateFormat="MMM d, yyyy h:mm aa"
                minDate={DateTime.now().toISODate()}
                maxDate={DateTime.now().plus({ days: 365 }).toISODate()}
                placeholder={"Mon DD, YYYY h:mm aa"}
                withPortal={true}
                showTimeSelect={true}
                customStyles={_.get(customStyles, "inputGroupField", {})}
                extraStyles={{}}
              />
              <PaxPicker
                customStyles={_.get(customStyles, "inputGroupField", {})}
                adults={pax.adults}
                childs={pax.children}
                children_ages={pax.children_ages}
                onClick={onClickPax}
                product_type="TRF"
              />
            </div>
            <div className={classes.searchBtnContr}>
              <SearchButton
                type="submit"
                text={t("search")}
                loading={false}
                customStyles={_.get(customStyles, "searchBtn", {})}
              />
            </div>
          </Form>
        </Formik>
      </div>
      {showPaxPickerModal && (
        <PaxPickerModal
          adults={pax.adults}
          childs={pax.children}
          children_ages={pax.children_ages}
          onFormUpdate={({ form: paxData }) => setPax(paxData)}
          onCancel={() => setShowPaxPickerModal(false)}
        />
      )}
    </div>
  );
};
DeepTransferSearchForm.propTypes = {};
export default DeepTransferSearchForm;
